import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';

import {
  boundaryValidationSchema,
  fieldDetailSchema,
  fieldListResponseSchema,
  fieldWriteResponseSchema,
  importedShapesResponseSchema,
  type BoundaryValidation,
  type FieldDetail,
  type FieldListResponse,
  type ImportedShapes,
  type SaveFieldBody,
  type ValidateBoundaryBody,
} from './schemas';

/**
 * Every saved Field with its Boundary geometry.
 *
 * **No `bbox`, ever** — 113586 §11c requires a Cluster marker's count to equal the exact number of
 * elements it represents, and a viewport slice cannot count what it never fetched (ADR-0016
 * § Loading). `search` is unused too: the search panel is served by `GET /map/tracking/search`.
 *
 * `getList` is the verb that keeps the `{ data, meta }` envelope. Its static `PageMeta` is not this
 * route's meta, so the value goes straight into zod, which produces the real shape.
 */
export async function getMapFields(): Promise<FieldListResponse> {
  const path = endpoints.fields.list;
  return parseContract(fieldListResponseSchema, await apiClient.getList(path), path);
}

/**
 * One Field for the details panel. Carries no `navigation` block and no `bounds`: stepping order is
 * derived from the list's `createdAt` and bounds from `boundary`, both client-side.
 */
export async function getField(fieldId: string): Promise<FieldDetail> {
  const path = endpoints.fields.detail(fieldId);
  return parseContract(fieldDetailSchema, await apiClient.get(path), path);
}

/**
 * Delete one Field (113586 §10a). No response body — the Boundary layer is re-read rather than
 * patched, because the legend and the no-fields empty state are both derived from that payload.
 *
 * Same path as `getField`: `DELETE /fields/tracking/{fieldId}` is the deployed contract's other verb
 * on the detail route, so there is no new `endpoints` entry.
 */
export async function deleteField(fieldId: string): Promise<void> {
  await apiClient.delete(endpoints.fields.detail(fieldId));
}

/**
 * The **Placement verdict** for a candidate geometry. A `POST` that writes nothing — the server
 * answers USA containment, overlap, duplicate coordinates and the ring verdict, all of which we read
 * (ADR-0018). Containment is batched server-side, so one call costs one round trip whatever the
 * vertex count.
 */
export async function validateBoundary(body: ValidateBoundaryBody): Promise<BoundaryValidation> {
  const path = endpoints.fields.validateBoundary;
  return parseContract(boundaryValidationSchema, await apiClient.post(path, body), path);
}

/**
 * Create the Field. The reply is parsed down to `{ id }` — everything else, `releasedDeviceIds`
 * included, is dropped on purpose: the caches are invalidated, never patched.
 */
export async function createField(body: SaveFieldBody): Promise<{ id: string }> {
  const path = endpoints.fields.list;
  return parseContract(fieldWriteResponseSchema, await apiClient.post(path, body), path);
}

/**
 * Update the Field being edited. Same body as {@link createField} and the same `{ id }` reply — an
 * edit differs from a create only in the verb and the path.
 */
export async function updateField(fieldId: string, body: SaveFieldBody): Promise<{ id: string }> {
  const path = endpoints.fields.detail(fieldId);
  return parseContract(fieldWriteResponseSchema, await apiClient.patch(path, body), path);
}

/**
 * Parse an uploaded shapefile ZIP into candidate **Imported shape**s.
 *
 * **Nothing is created and nothing is stored** — the server parses the buffer in memory and drops
 * it. The Grower places the shape, edits it in **Field Mapping mode**, and saves through the
 * existing `POST /fields/tracking` with `origin: 'imported'`.
 *
 * A bare `FormData` with one `file` part: axios sets the multipart boundary itself, so declaring a
 * `Content-Type` here would strip it and the server would reject the body. **Every** judgement of
 * the file is the server's — the client pre-checks size only (ticket 03) and never unzips, per
 * [ADR-0018](../../../docs/adr/0018-field-boundary-geometry-and-drawing.md), which both sets the
 * rule ("the client answers only what a browser can answer honestly") and bans the toolchain
 * (`no turf, no shpjs, no proj4`) that guessing the rest would need.
 *
 * `signal` is threaded so the Add New Field popover's `×` can abort a stalled upload (ticket 03).
 * It is load-bearing rather than a nicety: **no request timeout is configured anywhere in this
 * app**, so without it a stalled upload kills the Add Field flow until a page reload.
 */
export async function importShapefile(file: File, signal?: AbortSignal): Promise<ImportedShapes> {
  const path = endpoints.fields.import;
  const body = new FormData();
  body.append('file', file);
  return parseContract(
    importedShapesResponseSchema,
    await apiClient.post(path, body, { signal }),
    path,
  );
}
